import { ReactElement, ReactNode, useState, useEffect, useRef } from 'react';
import Spinner from './Spinner';
import { HiOutlinePencilSquare } from 'react-icons/hi2';

interface DetailCardProps {
  label: string;
  children: ReactNode | ((isEdit: boolean, setIsSaving: (saving: boolean) => void) => ReactNode);
  canEdit?: boolean;
  onSave?: () => void;
  excludeFromClickOutside?: string[]; // Array of CSS selectors to exclude
  className?: string; // Additional CSS classes
}

export default function DetailCard({
  label,
  children,
  canEdit = false,
  onSave,
  excludeFromClickOutside = [],
  className = '',
}: DetailCardProps): ReactElement {
  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleCardClick = () => {
    if (canEdit && !isEdit) {
      setIsEdit(true);
    }
  };

  const handleExitEdit = () => {
    if (onSave) {
      onSave();
    }
    setIsEdit(false);
  };

  // Check if element is part of an overlay/modal
  const isOverlayElement = (element: Element): boolean => {
    // Check if element is inside any of the excluded selectors
    for (const selector of excludeFromClickOutside) {
      if (element.closest(selector)) {
        return true;
      }
    }

    // Common overlay/modal selectors
    const overlaySelectors = [
      '[role="dialog"]',
      '[role="menu"]',
      '[role="listbox"]',
      '[role="combobox"]',
      '.MuiPopover-root',
      '.MuiModal-root',
      '.MuiPopper-root',
      '.react-datepicker',
      '.calendar-overlay',
      '.datepicker-overlay',
    ];

    for (const selector of overlaySelectors) {
      if (element.closest(selector)) {
        return true;
      }
    }

    return false;
  };

  // Handle click outside to exit edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isEdit || !cardRef.current) return;

      const target = event.target as Element;

      // Don't exit if click is inside the card
      if (cardRef.current.contains(target)) {
        return;
      }

      // Don't exit if click is on an overlay element
      if (isOverlayElement(target)) {
        return;
      }

      handleExitEdit();
    };

    // Handle keyboard events
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEdit && event.key === 'Escape') {
        handleExitEdit();
      }
    };

    if (isEdit) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isEdit, excludeFromClickOutside]);

  return (
    <div
      ref={cardRef}
      className={`cr-group cr-relative cr-flex cr-w-full cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center
                  cr-shadow-md md:cr-w-56
                  ${canEdit && !isEdit ? 'cr-cursor-pointer' : ''} ${className}`}
      onClick={handleCardClick}
    >
      {canEdit && !isEdit && !isSaving && (
        <div className="cr-absolute cr-right-2 cr-top-2 cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100">
          <HiOutlinePencilSquare className="cr-text-[20px] cr-text-[#03365D]" />
        </div>
      )}
      <div className="cr-absolute cr-right-2 cr-top-2">
        <Spinner isFetching={isSaving} />
      </div>
      <label className="font-montserrat cr-m-4 cr-text-[18px] cr-font-bold cr-uppercase cr-text-[#03365D]">{label}</label>
      <div className="cr-m-2 cr-w-full cr-overflow-hidden cr-font-main cr-text-[16px] cr-text-[#03365D]">
        {typeof children === 'function' ? children(isEdit, setIsSaving) : children}
      </div>
    </div>
  );
}
